/**
 * First-run wizard — SPEC §11.3.
 *
 * The highest-friction moment in the product, and the only path that works at all for the
 * nine instructors with no teaching history (§4.4). Three steps, shown once:
 *
 *   1. Confirm your details  — pre-filled; corrections notify Luke, never overwrite
 *   2. Your usual schedule   — the single most valuable screen in the product
 *   3. Your first month      — the pattern applied immediately, so the payoff is visible
 *      and they complete a real submission before they ever see the calendar
 *
 * ## Skipping is allowed, deliberately
 *
 * An instructor with no usual schedule can pass step 2 and fall back to the calendar (§11.3).
 * Forcing a pattern out of somebody who does not have one produces confident, wrong data
 * that then generates months of availability nobody meant.
 *
 * ## Onboarding is marked complete only at the end
 *
 * If they close the tab midway, the wizard is still there next time. Marking it done on
 * first render would strand a half-onboarded instructor on a blank calendar with no pattern
 * and no way back to the one screen that would have fixed it.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { formatCycle, type CalendarDate } from '@gearup/shared';
import {
  ApiError,
  api,
  horizonContextOf,
  type LocationOption,
  type MonthView,
  type Profile,
} from '../lib/api.js';
import { MonthCalendar } from '../components/MonthCalendar.js';
import { DefaultsReview } from '../components/DefaultsReview.js';
import {
  PatternBuilder,
  describeSlot,
  duplicateKeys,
  toSlotInput,
  type PatternDraft,
} from '../components/PatternBuilder.js';
import { useSession } from '../lib/session.js';

type Step = 'details' | 'pattern' | 'month';

export function Onboarding() {
  const { instructor, refresh } = useSession();
  const [step, setStep] = useState<Step>('details');
  const [profile, setProfile] = useState<Profile | null>(null);
  const [locations, setLocations] = useState<LocationOption[]>([]);
  const [today, setToday] = useState<CalendarDate | null>(null);
  const [slots, setSlots] = useState<PatternDraft[]>([]);
  const [month, setMonth] = useState<MonthView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [p, meta, existing] = await Promise.all([
      api.profile.get(),
      api.availability.locations(),
      api.pattern.get(),
    ]);
    setProfile(p);
    setLocations(meta.locations);
    setToday(meta.today);

    // Seed from whatever is already stored. Onboarding only completes at the last step, so
    // somebody who closed the tab after saving a pattern comes back to step 2 — and if that
    // started blank, pressing "I don't have a usual schedule" would silently delete the
    // pattern they had just entered.
    setSlots(
      existing.slots.map((slot, index) => ({
        key: `existing-${index}`,
        weekday: slot.weekday,
        frequency: slot.frequency,
        ...(slot.startTime ? { startTime: slot.startTime } : {}),
        period: slot.period,
        locationId: slot.locationId,
      })),
    );
  }, []);

  useEffect(() => {
    void load().catch(() => setError('Could not load your details. Please refresh.'));
  }, [load]);

  if (!profile || !today) return <p className="muted center">Loading…</p>;

  return (
    <div className="stack">
      <Progress step={step} />
      {error && (
        <div className="notice notice--error" role="alert">
          <span className="notice__icon" aria-hidden="true">!</span>
          <span>{error}</span>
        </div>
      )}

      {step === 'details' && (
        <DetailsStep
          profile={profile}
          firstName={instructor?.firstName ?? profile.firstName}
          onDone={() => setStep('pattern')}
        />
      )}

      {step === 'pattern' && (
        <PatternStep
          slots={slots}
          locations={locations}
          busy={busy}
          onChange={setSlots}
          onSkip={async () => {
            // Skipping still advances; the calendar is the fallback (SPEC §11.3).
            await goToMonth([]);
          }}
          onSave={async () => {
            await goToMonth(slots);
          }}
        />
      )}

      {step === 'month' && month && (
        <MonthStep
          month={month}
          locations={locations}
          today={today}
          hasPattern={slots.length > 0}
          busy={busy}
          onSubmit={submitMonth}
          onFinish={finish}
        />
      )}
    </div>
  );

  async function goToMonth(draft: PatternDraft[]) {
    setBusy(true);
    setError(null);
    try {
      await api.pattern.set(toSlotInput(draft));

      // Applying the pattern here is the point of step 3 — they see concrete dates rather
      // than being told the pattern was saved (SPEC §11.3).
      const { months, requiredCycle } = await api.availability.months();
      const target = months.find((m) => m.cycleKey === requiredCycle) ?? months[0]!;
      const filled = draft.length > 0 ? await api.availability.applyPattern(target.cycleKey) : target;

      setMonth(filled);
      setStep('month');
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "That didn't save. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function submitMonth() {
    if (!month) return;
    setBusy(true);
    setError(null);
    try {
      await api.availability.submit(month.cycleKey, {
        patternApplied: month.patternApplied,
        exceptions: [],
      });
      await finish();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send that. Please try again.');
      setBusy(false);
    }
  }

  /** Marks onboarding done and hands over to the normal month view. */
  async function finish() {
    setBusy(true);
    try {
      await api.markOnboarded();
      await refresh();
    } catch {
      // If this fails they simply see the wizard again, which is recoverable and honest.
      setError('Saved, but we could not close your setup. Refresh and it will pick up.');
      setBusy(false);
    }
  }
}

// ---------------------------------------------------------------------------

function Progress({ step }: { step: Step }) {
  const steps: { key: Step; label: string }[] = [
    { key: 'details', label: 'Your details' },
    { key: 'pattern', label: 'Usual schedule' },
    { key: 'month', label: 'First month' },
  ];
  const index = steps.findIndex((s) => s.key === step);
  return (
    <ol className="steps" aria-label={`Step ${index + 1} of ${steps.length}`}>
      {steps.map((s, i) => (
        <li
          key={s.key}
          className={`steps__step${i === index ? ' steps__step--on' : ''}${i < index ? ' steps__step--done' : ''}`}
          aria-current={i === index ? 'step' : undefined}
        >
          <span className="steps__dot" aria-hidden="true">{i < index ? '✓' : i + 1}</span>
          <span className="steps__label">{s.label}</span>
        </li>
      ))}
    </ol>
  );
}

function DetailsStep({
  profile,
  firstName,
  onDone,
}: {
  profile: Profile;
  firstName: string;
  onDone: () => void;
}) {
  const [reporting, setReporting] = useState(false);
  const [message, setMessage] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sites = useMemo(
    () =>
      [
        ...(profile.homeLocation ? [profile.homeLocation.canonicalName] : []),
        ...profile.additionalLocations.map((l) => l.canonicalName),
      ].join(', ') || 'None on file',
    [profile],
  );

  return (
    <div className="card stack">
      <h1 style={{ margin: 0 }}>Welcome, {firstName}</h1>
      <p className="muted" style={{ margin: 0 }}>
        This takes about a minute. First — is this right?
      </p>

      <dl className="details">
        <dt>Name</dt>
        <dd>{profile.firstName} {profile.lastName}</dd>
        <dt>Email</dt>
        <dd>{profile.email}</dd>
        <dt>{profile.additionalLocations.length ? 'Locations' : 'Location'}</dt>
        <dd>{sites}</dd>
      </dl>

      {sent && (
        <div className="notice notice--success" role="status">
          <span className="notice__icon" aria-hidden="true">✓</span>
          <span>Thanks — we've passed that on. Nothing has changed yet; the office will sort it.</span>
        </div>
      )}

      {error && (
        <div className="notice notice--error" role="alert">
          <span className="notice__icon" aria-hidden="true">!</span>
          <span>{error}</span>
        </div>
      )}

      {!reporting ? (
        <div className="stack">
          <button className="btn btn--primary btn--block" onClick={onDone}>
            Yes, that's right
          </button>
          {!sent && (
            <button className="btn btn--ghost btn--block" onClick={() => setReporting(true)}>
              Something's wrong
            </button>
          )}
        </div>
      ) : (
        <div className="stack">
          <label className="field">
            <span className="field__label">What needs changing?</span>
            <textarea
              className="textarea"
              value={message}
              autoFocus
              placeholder="e.g. My last name is spelled Saylor, not Sailor"
              onChange={(e) => setMessage(e.target.value)}
            />
          </label>
          <p className="small muted" style={{ margin: 0 }}>
            We'll pass this to the office rather than changing it here — some of these details
            are tied to how your classes are matched up.
          </p>
          <div className="row">
            <button
              className="btn btn--primary grow"
              disabled={busy || message.trim().length === 0}
              onClick={async () => {
                setBusy(true);
                setError(null);
                try {
                  await api.profile.reportCorrection(message.trim());
                  setSent(true);
                  setReporting(false);
                  setMessage('');
                } catch (err) {
                  setError(
                    err instanceof ApiError
                      ? err.message
                      : "We couldn't send that. Please email admin@gearupwithcpr.com.",
                  );
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? 'Sending…' : 'Send to the office'}
            </button>
            <button className="btn btn--secondary" disabled={busy} onClick={() => setReporting(false)}>
              Back
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function PatternStep({
  slots,
  locations,
  busy,
  onChange,
  onSkip,
  onSave,
}: {
  slots: PatternDraft[];
  locations: LocationOption[];
  busy: boolean;
  onChange: (s: PatternDraft[]) => void;
  onSkip: () => void;
  onSave: () => void;
}) {
  const hasClash = duplicateKeys(slots).size > 0;

  return (
    <div className="card stack">
      <h1 style={{ margin: 0 }}>Which days do you usually teach?</h1>
      <p className="muted" style={{ margin: 0 }}>
        We'll use this to fill in each month for you, so you only tell us what's different.
      </p>

      <PatternBuilder slots={slots} locations={locations} disabled={busy} onChange={onChange} />

      {slots.length > 0 && (
        <div className="notice notice--info">
          <span className="notice__icon" aria-hidden="true">ℹ</span>
          <span>
            Your usual month: <strong>{slots.map(describeSlot).join(', ')}</strong>
          </span>
        </div>
      )}

      <button
        className="btn btn--primary btn--block"
        disabled={busy || slots.length === 0 || hasClash}
        onClick={onSave}
      >
        {busy ? 'Saving…' : 'Continue'}
      </button>

      {/* Allowed on purpose (SPEC §11.3): forcing a pattern from somebody who has none
          produces confident, wrong data that generates months nobody meant. */}
      <button className="btn btn--ghost btn--block" disabled={busy} onClick={onSkip}>
        I don't have a usual schedule
      </button>
    </div>
  );
}

function MonthStep({
  month,
  locations,
  today,
  hasPattern,
  busy,
  onSubmit,
  onFinish,
}: {
  month: MonthView;
  locations: LocationOption[];
  today: CalendarDate;
  hasPattern: boolean;
  busy: boolean;
  onSubmit: () => void;
  onFinish: () => void;
}) {
  const label = formatCycle(month.cycleKey);
  // Dates and classes are no longer the same number: a day can hold two.
  const dayCount = new Set(month.occurrences.map((o) => o.date)).size;
  const classCount = month.occurrences.length;

  return (
    <div className="card stack">
      <h1 style={{ margin: 0 }}>{label}</h1>

      {hasPattern ? (
        <p className="muted" style={{ margin: 0 }}>
          Here's {label} filled in from your usual schedule —{' '}
          <strong>{dayCount} day{dayCount === 1 ? '' : 's'}</strong>
          {classCount > dayCount && <> carrying <strong>{classCount} classes</strong></>}. It
          doesn't count as your answer until you send it.
        </p>
      ) : (
        <p className="muted" style={{ margin: 0 }}>
          No usual schedule, so {label} is empty. You can mark the days you're free on the
          calendar once you're through here.
        </p>
      )}

      <MonthCalendar
        cycleKey={month.cycleKey}
        occurrences={month.occurrences}
        today={today}
        horizon={horizonContextOf(month)}
        onToggle={() => {}}
        disabled
      />

      {/* Before the submit button, not after it. Both instructors this exists for were
          first-time users who sent their month from right here. */}
      <DefaultsReview occurrences={month.occurrences} locations={locations} />

      {hasPattern && classCount > 0 ? (
        <div className="stack">
          <button className="btn btn--primary btn--block" disabled={busy} onClick={onSubmit}>
            {busy ? 'Sending…' : `Looks right — send ${label}`}
          </button>
          <button className="btn btn--ghost btn--block" disabled={busy} onClick={onFinish}>
            I need to change some dates first
          </button>
        </div>
      ) : (
        <button className="btn btn--primary btn--block" disabled={busy} onClick={onFinish}>
          {busy ? 'Finishing…' : 'Go to my calendar'}
        </button>
      )}
    </div>
  );
}
